import {
    getStartPageConfig,
    getTrendingBrands,
    getTrendingSearchesCms,
    getTrendingSearchesPersonalisedSuggestions,
    getWishListItems,
    getFavouriteSellers,
    getSellerSuggestions
} from './api';
import {
    setStartPageConfigAction,
    setStartPageConfigStatusAction,
    setTrendingBrandsAction,
    setTrendingBrandsStatusAction,
    setTrendingSearchesAction,
    setTrendingSearchesStatusAction,
    setWishListAction,
    setWishListStatusAction,
    setFavouriteSellersAction,
    setFavouriteSellersStatusAction
} from './reducer';
import {
    selectAutomaticTranslationPreference,
    selectPreferredLanguage
} from 'tradera-state/language/selectors';
import itemActions from 'tradera-state/items/actions';

const defaultStartPageConfig = {
    header: {
        alternativeText: '',
        backgroundImage: null,
        isCampaignMode: false,
        webpageUrl: ''
    },
    contentLoggedOut: [{ type: 'lastChance' }, { type: 'recentlyViewed' }],
    contentLoggedIn: [
        { type: 'wishList' },
        { type: 'favouriteSellers' },
        { type: 'lastChance' },
        { type: 'recentlyViewed' }
    ]
};

export const fetchStartPageConfig = () => async (dispatch, getState) => {
    const { status } = getState().views.startPage;
    if (status !== 'NOT_FETCHED') return;

    dispatch(setStartPageConfigStatusAction({ status: 'LOADING' }));

    const config = await getStartPageConfig(
        selectPreferredLanguage(getState()).languageCodeIso2
    );

    dispatch(
        setStartPageConfigAction({
            config: config || defaultStartPageConfig,
            status: 'HAS_DATA'
        })
    );
};

export const fetchTrendingBrandsIfNeeded = () => async (dispatch, getState) => {
    const { status } = getState().views.startPage.trendingBrands;
    if (status !== 'NOT_FETCHED') return;

    dispatch(setTrendingBrandsStatusAction({ status: 'LOADING' }));

    const brandsResult = await getTrendingBrands();

    const newStatus = brandsResult.length > 0 ? 'HAS_DATA' : 'HAS_NO_DATA';
    dispatch(
        setTrendingBrandsAction({ items: brandsResult, status: newStatus })
    );
};

export const fetchTrendingSearchesIfNeeded = () => async (
    dispatch,
    getState
) => {
    const { status } = getState().views.startPage.trendingSearches;

    if (status !== 'NOT_FETCHED') return;

    const isTrendingSearchesFromMlEnabled = getState().environment
        .featureSwitches['trending-searches-from-ml'];

    dispatch(setTrendingSearchesStatusAction({ status: 'LOADING' }));

    const searchesResult = isTrendingSearchesFromMlEnabled
        ? await getTrendingSearchesPersonalisedSuggestions()
        : await getTrendingSearchesCms();

    const newStatus = searchesResult.length > 0 ? 'HAS_DATA' : 'HAS_NO_DATA';
    dispatch(
        setTrendingSearchesAction({ items: searchesResult, status: newStatus })
    );
};

export const fetchWishList = () => async (dispatch, getState) => {
    const { status } = getState().views.startPage.wishList;
    if (status === 'LOADING') return;

    dispatch(setWishListStatusAction({ status: 'LOADING' }));

    const items = await getWishListItems();
    dispatch(itemActions.add(items));

    const itemIds = items.map(item => item.itemId);
    dispatch(itemActions.addItemsToWishList(itemIds));
    const newStatus = items.length > 0 ? 'HAS_DATA' : 'HAS_NO_DATA';
    dispatch(setWishListAction({ itemIds: itemIds, status: newStatus }));
};

export const fetchFavouriteSellers = () => async (dispatch, getState) => {
    const { status } = getState().views.startPage.favouriteSellers;
    if (status === 'LOADING') return;

    const isSellerSuggestionsEnabled = getState().environment.featureSwitches[
        'seller-suggestions'
    ];

    dispatch(setFavouriteSellersStatusAction({ status: 'LOADING' }));

    const shippingCountryCode = getState().shippingRegion.shippingCountry
        .countryCodeIso2;
    const userLanguageCodeIso2 = selectPreferredLanguage(getState())
        .languageCodeIso2;

    const automaticTranslationPreference = selectAutomaticTranslationPreference(
        getState()
    );
    const promise = getFavouriteSellers(
        shippingCountryCode,
        userLanguageCodeIso2,
        automaticTranslationPreference
    );
    const suggestionsPromise =
        isSellerSuggestionsEnabled && getSellerSuggestions(shippingCountryCode);
    const { favouriteSellers = [], items = [], totalItems = 0 } = await promise;
    dispatch(itemActions.add(items));

    const itemIds = items.map(item => item.itemId);

    const newStatus = totalItems > 0 ? 'HAS_DATA' : 'HAS_NO_DATA';
    dispatch(
        setFavouriteSellersAction({
            sellers: favouriteSellers,
            itemIds,
            totalItems,
            status: newStatus
        })
    );

    if (isSellerSuggestionsEnabled) {
        const sellerSuggestions = await suggestionsPromise;
        dispatch(
            setFavouriteSellersAction({
                sellerSuggestions
            })
        );
    }
};
